import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  PublicQuizAPI,
  ProgressResult,
  ScoreRegressionWarning,
} from '../../apis/PublicQuizAPI'
import apiClient from '../../apis/configs/AxiosClient'

const SIM_AUTH = 'Basic ' + btoa(
  (process.env.REACT_APP_BASIC_AUTH_USERNAME ?? 'admin') + ':' +
  (process.env.REACT_APP_BASIC_AUTH_PASSWORD ?? 'password')
)

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_DELAY_MS = 600   // pause between steps so the panel is readable

const SIM_FIRST_NAMES = [
  'James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph',
  'Thomas', 'Charles', 'Christopher', 'Daniel', 'Matthew', 'Anthony', 'Mark',
  'Donald', 'Steven', 'Paul', 'Andrew', 'Joshua', 'Kevin', 'Brian', 'George',
  'Timothy', 'Ronald', 'Edward', 'Jason', 'Jeffrey', 'Ryan', 'Jacob',
  'Mary', 'Patricia', 'Jennifer', 'Linda', 'Barbara', 'Elizabeth', 'Susan',
  'Jessica', 'Sarah', 'Karen', 'Lisa', 'Nancy', 'Betty', 'Margaret', 'Sandra',
  'Ashley', 'Dorothy', 'Kimberly', 'Emily', 'Donna', 'Michelle', 'Carol',
  'Amanda', 'Melissa', 'Deborah', 'Stephanie', 'Rebecca', 'Sharon', 'Laura',
  'Cynthia', 'Kathleen', 'Anna', 'Amy', 'Angela', 'Helen', 'Brenda', 'Samantha',
]

const SIM_LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
  'Lee', 'Thompson', 'White', 'Harris', 'Clark', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen',
  'Wright', 'Scott', 'Torres', 'Hill', 'Green',
  'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell',
  'Carter', 'Roberts', 'Turner', 'Phillips', 'Parker', 'Evans', 'Edwards',
  'Collins', 'Stewart', 'Morris', 'Rogers', 'Reed','Morgan',
  'Bell', 'Murphy', 'Bailey', 'Cooper', 'Richardson', 'Howard',
]

function randomSimName(): string {
  const first = SIM_FIRST_NAMES[Math.floor(Math.random() * SIM_FIRST_NAMES.length)]
  const last  = SIM_LAST_NAMES[Math.floor(Math.random() * SIM_LAST_NAMES.length)]
  return `${first} ${last}`
}

const LEVELS = ['Basic', 'Intermediate', 'Advanced', 'Highly advanced'] as const
type Level = typeof LEVELS[number]
type LevelPct = Record<Level, number>

const DEFAULT_LEVEL_PCTS: LevelPct[] = [
  { Basic:  25, Intermediate:  25, Advanced:  25, 'Highly advanced':  25 },
  { Basic: 100, Intermediate: 100, Advanced:  25, 'Highly advanced':  25 },
  { Basic: 100, Intermediate: 100, Advanced: 100, 'Highly advanced':  25 },
  { Basic: 100, Intermediate: 100, Advanced: 100, 'Highly advanced': 100 },
]

const LEVEL_MAP: Record<string, { num: number; color: string; bg: string }> = {
  Basic:             { num: 1, color: '#7ba8ff', bg: 'rgba(64,97,194,0.55)' },
  Intermediate:      { num: 2, color: '#4fe0eb', bg: 'rgba(0,120,129,0.55)' },
  Advanced:          { num: 3, color: '#c4a8ff', bg: 'rgba(137,103,200,0.55)' },
  'Highly advanced': { num: 4, color: '#ffb86b', bg: 'rgba(255,130,0,0.55)' },
}
// Thresholds match backend levelFromScore: [1,2)=Basic, [2,3)=Int, [3,4)=Adv, [4,5)=H.A
const scoreToLevel = (s: number) =>
  s < 2 ? 'Basic' : s < 3 ? 'Intermediate' : s < 4 ? 'Advanced' : 'Highly advanced'

// ─── Single simulator panel ──────────────────────────────────────────────────

function generateUniqueSimNames(count: number): string[] {
  const names: string[] = []
  const used = new Set<string>()
  while (names.length < count) {
    const n = randomSimName()
    if (!used.has(n)) { used.add(n); names.push(n) }
  }
  return names
}

interface PanelProps {
  slug: string
  panelIndex: number
  simName: string
  running: boolean
  maxQuestions: number
  resetKey: number
  onRegression: (w: ScoreRegressionWarning[]) => void
  onComplete: () => void
}

function SimulatorPanel({ slug, panelIndex, simName, running, maxQuestions, resetKey, onRegression, onComplete }: PanelProps) {
  const maxQuestionsRef = useRef(maxQuestions)
  useEffect(() => { maxQuestionsRef.current = maxQuestions }, [maxQuestions])
  const storageKey = `sim_level_pct_${slug}_${panelIndex}`
  const defaultPct  = DEFAULT_LEVEL_PCTS[panelIndex] ?? DEFAULT_LEVEL_PCTS[0]
  const [levelPct, setLevelPct] = useState<LevelPct>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) return { ...defaultPct, ...JSON.parse(saved) }
    } catch {}
    return { ...defaultPct }
  })
  const [levelPctStr, setLevelPctStr] = useState<Record<Level, string>>(() =>
    Object.fromEntries(LEVELS.map((l) => [l, String(levelPct[l])])) as Record<Level, string>
  )
  const levelPctRef = useRef(levelPct)
  useEffect(() => {
    levelPctRef.current = levelPct
    try { localStorage.setItem(storageKey, JSON.stringify(levelPct)) } catch {}
  }, [levelPct, storageKey])

  const stopStorageKey = `sim_stop_level_${slug}_${panelIndex}`
  const [stopAtLevel, setStopAtLevel] = useState<Level | null>(() => {
    try {
      const saved = localStorage.getItem(`sim_stop_level_${slug}_${panelIndex}`)
      if (saved && LEVELS.includes(saved as Level)) return saved as Level
    } catch {}
    return null
  })
  const stopAtLevelRef = useRef<Level | null>(stopAtLevel)
  useEffect(() => {
    stopAtLevelRef.current = stopAtLevel
    try {
      if (stopAtLevel) localStorage.setItem(stopStorageKey, stopAtLevel)
      else localStorage.removeItem(stopStorageKey)
    } catch {}
  }, [stopAtLevel, stopStorageKey])

  const LEVEL_ORDER: Record<Level, number> = { Basic: 1, Intermediate: 2, Advanced: 3, 'Highly advanced': 4 }

  const [progress, setProgress]     = useState<ProgressResult | null>(null)
  const [status, setStatus]         = useState<string>('Idle')
  const [showSummary, setShowSummary] = useState(false)
  // key: "compCode|type|level", value: [correct, incorrect]
  type AnswerTally = Record<string, [number, number]>
  const [answerTally, setAnswerTally] = useState<AnswerTally>({})
  const answerTallyRef = useRef<AnswerTally>({})
  // key: "compCode|type|level", value: { qNum, correct } of last answer
  const [lastAnswer, setLastAnswer] = useState<Record<string, { qNum: number; correct: boolean }>>({})
  const lastAnswerRef = useRef<Record<string, { qNum: number; correct: boolean }>>({})
  // key: "compCode|type|level", full ordered list of Q answers
  type CellHistory = Record<string, { qNum: number; correct: boolean }[]>
  const [cellHistory, setCellHistory] = useState<CellHistory>({})
  const cellHistoryRef = useRef<CellHistory>({})
  const [qCount, setQCount]         = useState(0)
  const qCountRef = useRef(0)
  // AI-Explicit: [correct, total] for the stats bar chip
  const [aiExplicitTally, setAiExplicitTally] = useState<[number, number]>([0, 0])
  const aiExplicitTallyRef = useRef<[number, number]>([0, 0])

  const loopRef    = useRef(false)
  const attemptId  = useRef<string | null>(null)
  const resultCode = useRef<string | null>(null)

  const step = useCallback(async () => {
    // ── Ensure we have an active attempt ──────────────────────────────────
    if (!attemptId.current) {
      setStatus('Starting attempt…')
      try {
        const res = await PublicQuizAPI.start({
          name:           simName,
          email:          `sim+${Date.now()}@simulator.local`,
          assessmentSlug: slug,
        })
        attemptId.current = res.attemptId
      } catch {
        setStatus('Failed to start attempt')
        loopRef.current = false
        return
      }
    }

    // ── Get next question ─────────────────────────────────────────────────
    let next: Awaited<ReturnType<typeof PublicQuizAPI.nextQuestion>>
    try {
      // admin token forwarded from URL so answerIndex is included — needed for server-side evaluation
      next = await PublicQuizAPI.nextQuestion(attemptId.current, new URLSearchParams(window.location.search).get('admin'))
    } catch {
      setStatus('Error fetching question')
      loopRef.current = false
      return
    }

    if (!next) {
      setStatus('Complete ✓')
      loopRef.current = false
      onComplete()
      // fetch final progress and resultCode
      const prog = await PublicQuizAPI.getProgress(attemptId.current).catch(() => null)
      if (prog) setProgress(prog)
      const info = await PublicQuizAPI.getAttemptInfo(attemptId.current).catch(() => null)
      if (info?.resultCode) resultCode.current = info.resultCode
      return
    }

    // ── Submit answer ─────────────────────────────────────────────────────
    const pct = levelPctRef.current[next.level as Level] ?? 50
    const correct = Math.random() * 100 < pct
    const tallyKey = `${next.competenceId.replace('Competence/', '')}|${next.outcomeType}|${next.level}`
    if (next.question.aiLabel === 'AI-Explicit') {
      const [c, t] = aiExplicitTallyRef.current
      const updatedTally: [number, number] = [c + (correct ? 1 : 0), t + 1]
      aiExplicitTallyRef.current = updatedTally
      setAiExplicitTally(updatedTally)
    }
    const prev = answerTallyRef.current[tallyKey] ?? [0, 0]
    const updated = { ...answerTallyRef.current, [tallyKey]: [prev[0] + (correct ? 1 : 0), prev[1] + (correct ? 0 : 1)] as [number, number] }
    answerTallyRef.current = updated
    setAnswerTally(updated)
    const updatedLastAnswer = { ...lastAnswerRef.current, [tallyKey]: { qNum: qCountRef.current + 1, correct } }
    lastAnswerRef.current = updatedLastAnswer
    setLastAnswer(updatedLastAnswer)
    const prevHistory = cellHistoryRef.current[tallyKey] ?? []
    const updatedHistory = { ...cellHistoryRef.current, [tallyKey]: [...prevHistory, { qNum: qCountRef.current + 1, correct }] }
    cellHistoryRef.current = updatedHistory
    setCellHistory(updatedHistory)
    setStatus(`${next.competenceId.replace('Competence/', '')} ${next.outcomeType} ${next.level} → ${correct ? '✓' : '✗'}`)
    try {
      // Derive the option key to submit based on the random correct/wrong outcome.
      // correct=true  → send the actual correct option key (known via answerIndex from admin endpoint)
      // correct=false → send an explicit incorrect option key so the attempt stores a real selected answer
      const correctKey = next.question.options[next.question.answerIndex]?.key
      const wrongKey = next.question.options.find((option) => option.key !== correctKey)?.key
      const selectedOptionKeys = correct
        ? (correctKey ? [correctKey] : [])
        : (wrongKey ? [wrongKey] : [])
      const result = await PublicQuizAPI.submitAnswer(attemptId.current, next.question._id, selectedOptionKeys)
      if (correct && result.regressions.length > 0) {
        onRegression(result.regressions)
      }
    } catch {
      setStatus('Error submitting answer')
      loopRef.current = false
      return
    }

    qCountRef.current += 1
    setQCount(qCountRef.current)

    // ── Refresh progress ──────────────────────────────────────────────────
    const prog = await PublicQuizAPI.getProgress(attemptId.current).catch(() => null)
    if (prog) setProgress(prog)

    // ── Check stop trigger ────────────────────────────────────────────────
    const stopLevel = stopAtLevelRef.current
    if (prog && stopLevel) {
      const stopOrd = LEVEL_ORDER[stopLevel]
      // Every competence must have at least one answered KSA type,
      // AND every answered type must be exactly at stopLevel.
      const allAtLevel = prog.areas.every((area) =>
        area.competences.every((comp) => {
          const activeTypes = comp.types.filter((t) => t.directAnswerCount > 0)
          if (activeTypes.length === 0) return false   // unanswered competence → not done
          return activeTypes.every((t) => LEVEL_ORDER[t.estimatedLevel as Level] === stopOrd)
        })
      )
      if (allAtLevel) {
        setStatus(`Stopped: all KSA = ${stopLevel} ✓`)
        loopRef.current = false
        onComplete()
        return
      }
    }

    // ── Schedule next step ────────────────────────────────────────────────
    if (loopRef.current) {
      setTimeout(step, STEP_DELAY_MS)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simName, onRegression, onComplete])

  // ── Hard reset when resetKey changes ─────────────────────────────────────
  useEffect(() => {
    loopRef.current = false
    attemptId.current = null
    resultCode.current = null
    qCountRef.current = 0
    setQCount(0)
    setProgress(null)
    setStatus('Idle')
    setShowSummary(false)
    answerTallyRef.current = {}
    setAnswerTally({})
    lastAnswerRef.current = {}
    setLastAnswer({})
    cellHistoryRef.current = {}
    setCellHistory({})
    aiExplicitTallyRef.current = [0, 0]
    setAiExplicitTally([0, 0])
    // do NOT reset stopAtLevel — user wants to keep their setting
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // ── React to global running toggle ────────────────────────────────────────
  useEffect(() => {
    if (running && !loopRef.current) {
      loopRef.current = true
      step()
    } else if (!running) {
      loopRef.current = false
      setStatus((s) => {
        if (s === 'Idle' || s.startsWith('Complete') || s.startsWith('Stopped')) return s
        return s + ' (paused)'
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])

  // ── Reset when running restarts after complete ─────────────────────────────
  useEffect(() => {
    if (!running) return
    if (status.startsWith('Complete')) {
      attemptId.current = null
      resultCode.current = null
      qCountRef.current = 0
      setQCount(0)
      setProgress(null)
      setStatus('Restarting…')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      {/* Panel header: per-level % inputs + status */}
      <div className="flex-shrink-0 border-b border-white/10">
        <div className="flex gap-1 px-3 py-2">
          {LEVELS.map((lvl) => {
            const lm = LEVEL_MAP[lvl]
            return (
              <label key={lvl} className="flex-1 flex flex-col items-center gap-0.5">
                <span className="text-[9px] font-bold font-mono truncate w-full text-center" style={{ color: lm.color }}>
                  {lvl}
                </span>
                <div className="flex items-center gap-0.5">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={levelPctStr[lvl]}
                    onChange={(e) => {
                      const str = e.target.value
                      setLevelPctStr((prev) => ({ ...prev, [lvl]: str }))
                      const num = parseInt(str, 10)
                      if (!isNaN(num) && num >= 0 && num <= 100) {
                        setLevelPct((prev) => ({ ...prev, [lvl]: num }))
                      }
                    }}
                    className="w-12 bg-neutral-800 border border-white/20 rounded px-1.5 py-0.5 text-white text-[10px] text-right focus:outline-none focus:border-brand-yellow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-[9px] text-white/40">%</span>
                </div>
              </label>
            )
          })}
        </div>
        {/* Stop-trigger: single-select level */}
        <div className="flex gap-1 px-3 pb-1.5">
          <span className="text-[9px] text-white/30 font-mono flex-shrink-0 pt-0.5">stop@</span>
          <div className="flex flex-1 gap-1">
            {LEVELS.map((lvl) => {
              const lm = LEVEL_MAP[lvl]
              const active = stopAtLevel === lvl
              return (
                <button
                  key={lvl}
                  onClick={() => setStopAtLevel(active ? null : lvl)}
                  title={`Stop when all KSA ≥ ${lvl}`}
                  className={`flex-1 rounded text-[9px] font-bold font-mono py-0.5 transition-all ${
                    active
                      ? 'text-black'
                      : 'text-white/30 bg-white/5 hover:bg-white/10'
                  }`}
                  style={active ? { background: lm.color } : undefined}
                >
                  {lvl === 'Highly advanced' ? 'H.Adv' : lvl.slice(0, 4)}
                </button>
              )
            })}
          </div>
        </div>
        <div className="px-3 pb-2 flex items-center gap-2">
          <span className="text-[10px] text-white/50 font-mono font-bold flex-shrink-0">Q{qCount}</span>
          <span className="flex-1 text-[10px] text-white/40 font-mono truncate">{status}</span>
          {progress && qCount > 0 && (
            <>
              <button
                onClick={() => setShowSummary(true)}
                className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/60 hover:bg-white/20 hover:text-white transition-all"
              >
                Summary
              </button>
              <button
                onClick={() => { const d = process.env.REACT_APP_REPORT_DOMAIN ?? ''; const proto = d.startsWith('localhost') ? 'http' : 'https'; const code = resultCode.current ?? attemptId.current; window.open(`${proto}://${d}/?v=3&resultCode=${code}`, '_blank') }}
                className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/60 hover:bg-white/20 hover:text-white transition-all"
              >
                Report
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary modal */}
      {showSummary && progress && (() => {
        const TYPE_ABBR: Record<string, string> = { Knowledge: 'K', Skill: 'S', Attitude: 'A' }
        const LEVEL_ABBR: Record<string, string> = { Basic: 'Bas', Intermediate: 'Int', Advanced: 'Adv', 'Highly advanced': 'H.A' }
        const lines: string[] = []
        progress.areas.forEach((area) => {
          const areaScore = area.score != null ? `  ${area.score.toFixed(2)}  ${LEVEL_ABBR[scoreToLevel(area.score)] ?? ''}  conf ${Math.round(area.confidence * 100)}%` : ''
          lines.push(`── Area ${area.areaCode}${areaScore} ──`)
          area.competences.forEach((comp) => {
            // Include types that were directly answered OR have an inferred score (unasked but has siblings)
            const activeTypes = comp.types.filter((t) => t.directAnswerCount > 0 || (t as any).isInferred)
            if (activeTypes.length === 0) return
            const score = comp.score != null ? comp.score.toFixed(2) : '?   '
            const lvl = LEVEL_ABBR[comp.estimatedLevel] ?? comp.estimatedLevel
            const evidence = `n=${comp.directAnswerCount}`
            lines.push(`  ${comp.code.padEnd(8)} ${lvl}  ${score}  ${evidence}`)
            activeTypes.forEach((t) => {
              const tAbbr = (TYPE_ABBR[t.type] ?? t.type[0]).padEnd(1)
              const tLvl = LEVEL_ABBR[t.estimatedLevel] ?? t.estimatedLevel
              const tScore = t.score != null ? t.score.toFixed(2) : '?   '
              const tEvidence = `n=${t.directAnswerCount}`
              // per-level α/β + Q history from local tracking
              const levelLines = LEVELS
                .map((lvl) => {
                  const tk = `${comp.code}|${t.type}|${lvl}`
                  const history = cellHistory[tk]
                  const levelBelief = t.levels.find((lb) => lb.level === lvl)
                  const abStr = levelBelief ? `α=${levelBelief.alpha.toFixed(1)} β=${levelBelief.beta.toFixed(1)}` : ''
                  const noLo = levelBelief ? !levelBelief.hasLo : false
                  const noLoNote = noLo ? '  [no LO]' : ''
                  const isEst = lvl === t.estimatedLevel ? '*' : ' '
                  if (!history || history.length === 0) {
                    if (!abStr) return null
                    return `      ${isEst}${LEVEL_ABBR[lvl] ?? lvl}  ${abStr}${noLoNote}`
                  }
                  const qs = history.map((h) => `Q${h.qNum}${h.correct ? '✓' : '✗'}`).join(' ')
                  return noLo
                    ? `      ${isEst}${LEVEL_ABBR[lvl] ?? lvl}  ${abStr}  [no LO — tested Qs: ${qs}]`
                    : `      ${isEst}${LEVEL_ABBR[lvl] ?? lvl}  ${abStr}  ${qs}`
                })
                .filter(Boolean) as string[]
              lines.push(`    ${tAbbr}  ${tLvl}  ${tScore}  ${tEvidence}`)
              lines.push(...levelLines)
            })
          })
          lines.push('')
        })
        const text = lines.join('\n').trimEnd()
        return (
          <div
            className="absolute inset-0 z-50 bg-black/80 flex flex-col overflow-hidden"
            onClick={() => setShowSummary(false)}
          >
            <div
              className="m-2 flex-1 overflow-y-auto rounded-lg bg-neutral-900 border border-white/10 p-3 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <span className="text-white text-xs font-bold">Summary after Q{qCount}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigator.clipboard.writeText(text)}
                    className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/60 hover:bg-white/20 hover:text-white transition-all"
                  >
                    Copy
                  </button>
                  <button onClick={() => setShowSummary(false)} className="text-white/40 hover:text-white text-xs">✕</button>
                </div>
              </div>
              <pre className="flex-1 text-[11px] font-mono text-white/70 whitespace-pre select-all leading-relaxed overflow-y-auto">
                {text}
              </pre>
            </div>
          </div>
        )
      })()}
      <div className="flex-1 overflow-y-auto py-1">
        {progress && (() => {
          // Use server-computed scores directly
          const hasAnyActive = progress.areas.some(a => a.competences.some(c => c.directAnswerCount > 0))
          if (!hasAnyActive) return null
          const overallScore = progress.overallScore ?? null
          const kScore = progress.kScore ?? null
          const sScore = progress.sScore ?? null
          const aScore = progress.aScore ?? null
          const lvlLabel = (s: number | null) => s === null ? '·' : scoreToLevel(s)
          const fmt = (s: number | null) => s === null ? '·' : s.toFixed(2)
          const [aiCorrect, aiTotal] = aiExplicitTally
          const aiPct = aiTotal > 0 ? Math.round(aiCorrect / aiTotal * 100) : null
          return (
            <div className="flex gap-1 px-3 pt-2 pb-2 border-b border-white/10">
              {([
                { label: 'Overall', score: overallScore, color: '#ffffff' },
                { label: 'K', score: kScore, color: LEVEL_MAP[kScore !== null ? scoreToLevel(kScore) : 'Basic']?.color ?? '#fff' },
                { label: 'S', score: sScore, color: LEVEL_MAP[sScore !== null ? scoreToLevel(sScore) : 'Basic']?.color ?? '#fff' },
                { label: 'A', score: aScore, color: LEVEL_MAP[aScore !== null ? scoreToLevel(aScore) : 'Basic']?.color ?? '#fff' },
              ] as const).map(({ label, score, color }) => {
                const lm = score !== null ? LEVEL_MAP[scoreToLevel(score)] : null
                return (
                  <div key={label} className="flex-1 flex flex-col items-center rounded bg-white/5 px-1 py-1 gap-0.5">
                    <span className="text-[9px] font-bold uppercase tracking-wide text-white/40">{label}</span>
                    {lm ? (
                      <>
                        <span className="text-[11px] font-bold font-mono leading-none" style={{ color: lm.color }}>{fmt(score)}</span>
                        <span className="text-[9px] font-mono text-white/40">{lvlLabel(score)}</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-white/20">·</span>
                    )}
                  </div>
                )
              })}
              <div className="flex-1 flex flex-col items-center rounded bg-white/5 px-1 py-1 gap-0.5">
                <span className="text-[9px] font-bold uppercase tracking-wide text-white/40">AI</span>
                {aiPct !== null ? (
                  <>
                    <span className="text-[11px] font-bold font-mono leading-none" style={{ color: '#34d399' }}>{aiPct}%</span>
                    <span className="text-[9px] font-mono text-white/40">{aiCorrect}/{aiTotal}</span>
                  </>
                ) : (
                  <span className="text-[10px] text-white/20">·</span>
                )}
              </div>
            </div>
          )
        })()}
        {progress?.areas.map((area) => {
          const hasAreaData = area.score !== null && area.score !== undefined
          const areaLm = hasAreaData ? LEVEL_MAP[scoreToLevel(area.score!)] : null
          return (
            <div key={area.areaCode} className="mb-1">
              <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                <span className={`w-20 flex-shrink-0 text-sm font-bold ${hasAreaData ? 'text-white/90' : 'text-white/25'}`}>
                  Area {area.areaCode}
                </span>
                {hasAreaData && areaLm && (
                  <>
                    <span
                      className="flex-shrink-0 text-[11px] font-bold font-mono rounded px-1.5 py-0.5 leading-none"
                      style={{ color: areaLm.color, background: areaLm.bg }}
                    >
                      {area.score!.toFixed(2)}
                    </span>
                    <span className="text-[11px] font-mono text-white/40">{Math.round(area.confidence * 100)}%</span>
                  </>
                )}
              </div>

              {area.competences.map((comp) => {
                const lm = LEVEL_MAP[comp.estimatedLevel]
                const hasData = comp.directAnswerCount > 0
                return (
                  <div key={comp.code} className="mx-1 mb-px">
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded text-sm font-mono hover:bg-white/5">
                      <span className={`w-20 flex-shrink-0 text-xs ${hasData ? 'text-white/70' : 'text-white/25'}`}>
                        Comp {comp.code}
                      </span>
                      {hasData && lm ? (
                        <>
                          <span
                            className="flex-shrink-0 text-[11px] font-bold font-mono rounded px-1.5 py-0.5 leading-none"
                            style={{ color: lm.color, background: lm.bg }}
                          >
                            {comp.score != null ? comp.score.toFixed(2) : lm.num}
                          </span>
                          <span className="text-[11px] font-mono text-white/40">{Math.round(comp.confidence * 100)}%</span>
                        </>
                      ) : (
                        <span className="text-white/30 text-xs">·</span>
                      )}
                    </div>

                    {comp.types && comp.types.length > 0 && (
                      <div className="pl-2 pr-1 pb-1.5 space-y-0.5">
                        {comp.types.map((td) => {
                          const typeAbbr = ({ Knowledge: 'K', Skill: 'S', Attitude: 'A' } as Record<string, string>)[td.type] ?? td.type[0]
                          const isInferredType = (td as any).isInferred === true
                          const hasType   = td.directAnswerCount > 0 || isInferredType
                          const typeLm    = hasType ? LEVEL_MAP[td.estimatedLevel] : null
                          return (
                            <div key={td.type} className="flex items-center gap-1.5 px-1">
                              <span className={`text-[10px] font-bold w-4 flex-shrink-0 ${td.directAnswerCount > 0 ? 'text-brand-yellow' : hasType ? 'text-white/50' : 'text-white/25'}`}>
                                {typeAbbr}
                              </span>
                              {hasType && typeLm ? (
                                <span className="text-[10px] font-mono w-16 flex-shrink-0" style={{ color: isInferredType ? 'rgba(255,255,255,0.4)' : typeLm.color }}>
                                  {td.score != null ? td.score.toFixed(2) : typeLm.num}{' '}
                                  <span className="text-white/40">{isInferredType ? '~' : `${Math.round(td.confidence * 100)}%`}</span>
                                </span>
                              ) : (
                                <span className="text-white/20 text-[10px] w-16 flex-shrink-0">·</span>
                              )}
                              <div className="flex-1 flex gap-1">
                                {(() => {
                                  // Use td.estimatedLevel (server-authoritative) as the highlight.
                                  // The server applies framework-limited substitution and other
                                  // corrections that raw belief argmax doesn't reflect — e.g. when
                                  // a type has only low-level LOs and its argmax is inflated by
                                  // nudges but overridden by sibling-mean substitution.
                                  // Don't highlight at all for unasked slots (directAnswerCount=0).
                                  const hasEvidence = td.directAnswerCount > 0 || isInferredType
                                  return td.levels.map((lb) => {
                                    const isEst       = hasEvidence && lb.level === td.estimatedLevel
                                    const isNextProbe = lb.level === td.nextProbeLevel
                                    const cellKey     = `${comp.code}|${td.type}|${lb.level}`
                                    const cellHistory2 = cellHistory[cellKey] ?? []
                                    return (
                                      <span
                                        key={lb.level}
                                        className={`relative flex-1 text-center text-[10px] font-mono tabular-nums rounded py-0.5 leading-tight ${
                                          isEst ? 'text-white font-bold' : 'text-white/40'
                                        }`}
                                        style={{
                                          background: isEst ? 'rgba(255,219,0,0.18)' : 'rgba(255,255,255,0.05)',
                                          outline: isNextProbe
                                            ? '1px solid rgba(255,255,255,0.35)'
                                            : !lb.hasLo
                                            ? '1px dashed rgba(255,255,255,0.55)'
                                            : undefined,
                                          outlineOffset: '1px',
                                        }}
                                      >
                                        {cellHistory2.length > 0 && (
                                          <span className="absolute top-0 right-0 flex flex-col items-end leading-none">
                                            {cellHistory2.map((h) => (
                                              <span
                                                key={h.qNum}
                                                className="text-[9px] font-bold px-px"
                                                style={{ color: h.correct ? '#4ade80' : '#f87171' }}
                                              >
                                                {h.qNum}
                                              </span>
                                            ))}
                                          </span>
                                        )}
                                        <div>{lb.p.toFixed(2)}</div>
                                        <div className={`text-[9px] ${isEst ? 'text-white/80' : 'text-white/50'}`}>{lb.alpha}/{lb.beta}</div>
                                        {lb.aiExplicitCount > 0 && (
                                          <span className="absolute bottom-0 left-0 text-[7px] font-bold text-emerald-400 leading-none px-px">ai:{lb.aiExplicitCount}</span>
                                        )}
                                      </span>
                                    )
                                  })
                                })()}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
        {!progress && (
          <div className="px-3 py-4 text-xs text-white/40 text-center">Not started</div>
        )}
      </div>
    </div>
  )
}

// ─── Simulator page ─────────────────────────────────────────────────────────

const PANEL_COUNT = 4
const DEFAULT_MAX_Q = 40

interface AssessmentOption { slug: string; title: string }

export default function SimulatorPage() {
  const { slug: slugParam } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location  = useLocation()
  const [running, setRunning]               = useState(false)
  const [hasStarted, setHasStarted]         = useState(false)
  const [resetKey, setResetKey]             = useState(0)
  const [completedCount, setCompletedCount] = useState(0)
  const [regressionToast, setRegressionToast] = useState<ScoreRegressionWarning[] | null>(null)
  const [panelNames, setPanelNames]         = useState<string[]>(() => generateUniqueSimNames(PANEL_COUNT))

  // ── Assessment dropdown ───────────────────────────────────────────────────
  const [assessments, setAssessments]   = useState<AssessmentOption[]>([])
  const [slug, setSlug]                 = useState(slugParam ?? '')

  // Keep URL in sync whenever slug changes (preserve existing query params e.g. ?admin=)
  useEffect(() => {
    if (slug) navigate(`/sim/${slug}${location.search}`, { replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    apiClient.get('/v3/assessments', { headers: { Authorization: SIM_AUTH } })
      .then((res) => {
        const discrete = (res.data as any[])
          .filter((a) => a.adaptiveConfig?.modelType === 'discrete')
          .map((a) => ({ slug: a.slug, title: a.title || a.slug }))
        setAssessments(discrete)
        if (discrete.length && !discrete.find((a) => a.slug === slug)) {
          setSlug(discrete[0].slug)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Max questions — loaded from DB, saved to DB ─────────────────────────────
  const [maxQuestions, setMaxQuestions]       = useState<number>(DEFAULT_MAX_Q)
  const [draftMaxQStr, setDraftMaxQStr]       = useState<string>('')
  const [maxQLoaded, setMaxQLoaded]           = useState(false)
  const [savingMaxQ, setSavingMaxQ]           = useState(false)

  // Load questionCap whenever the selected assessment changes
  useEffect(() => {
    if (!slug) return
    setMaxQLoaded(false)
    setDraftMaxQStr('')
    apiClient.get(`/v3/assessments/${slug}`, { headers: { Authorization: SIM_AUTH } })
      .then((res) => {
        const cap: number | undefined = res.data?.adaptiveConfig?.questionCap
        if (cap != null) {
          setMaxQuestions(cap)
          setDraftMaxQStr(String(cap))
        }
        setMaxQLoaded(true)
      })
      .catch(() => { setMaxQLoaded(true) })
  }, [slug])

  const parsedDraftMaxQ = parseInt(draftMaxQStr, 10)
  const draftMaxQValid = !isNaN(parsedDraftMaxQ) && parsedDraftMaxQ >= 1
  const showSaveMaxQ = maxQLoaded && draftMaxQValid && parsedDraftMaxQ !== maxQuestions

  const handleSaveMaxQ = async () => {
    if (!draftMaxQValid) return
    setSavingMaxQ(true)
    try {
      await apiClient.patch(
        `/v3/assessments/${slug}`,
        { 'adaptiveConfig.questionCap': parsedDraftMaxQ },
        { headers: { Authorization: SIM_AUTH } },
      )
      setMaxQuestions(parsedDraftMaxQ)
    } catch {
      // silently keep draft so user can retry
    } finally {
      setSavingMaxQ(false)
    }
  }

  const allDone = completedCount >= PANEL_COUNT

  const handleRegression = useCallback((w: ScoreRegressionWarning[]) => {
    setRegressionToast(w)
  }, [])

  const handleComplete = useCallback(() => {
    setCompletedCount((c) => {
      const next = c + 1
      if (next >= PANEL_COUNT) setRunning(false)
      return next
    })
  }, [])

  const handleToggle = () => {
    if (allDone) {
      setCompletedCount(0)
      setHasStarted(false)
    }
    if (!running) setHasStarted(true)
    setRunning((r) => !r)
  }

  const handleRestart = () => {
    setRunning(false)
    setCompletedCount(0)
    setResetKey((k) => k + 1)
    setPanelNames(generateUniqueSimNames(PANEL_COUNT))
    setHasStarted(true)
    // brief timeout so panels finish resetting before restarting
    setTimeout(() => setRunning(true), 50)
  }

  const handleSlugChange = (newSlug: string) => {
    if (newSlug === slug) return
    setRunning(false)
    setCompletedCount(0)
    setHasStarted(false)
    setResetKey((k) => k + 1)
    setSlug(newSlug)
  }

  return (
    <div className="h-screen bg-neutral-950 flex flex-col overflow-hidden text-white">

      {/* ── Top bar ── */}
      <header className="flex-shrink-0 border-b border-white/10 px-6 py-3 flex items-center gap-4 flex-wrap">
        <span className="text-white font-bold text-sm tracking-wide flex-shrink-0">Discrete Engine Simulator</span>

        {/* Quiz select */}
        <select
          value={slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          disabled={running}
          className="bg-neutral-800 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-brand-yellow disabled:opacity-40 min-w-0 max-w-[18rem] truncate"
        >
          {assessments.length === 0 && (
            <option value={slug}>{slug}</option>
          )}
          {assessments.map((a) => (
            <option key={a.slug} value={a.slug}>{a.title}</option>
          ))}
        </select>

        {/* Max questions input */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <label className="text-white/40 text-[10px] uppercase tracking-widest">Max&nbsp;Q</label>
          <input
            type="number"
            min={1}
            max={200}
            value={draftMaxQStr}
            onChange={(e) => setDraftMaxQStr(e.target.value)}
            disabled={running || !maxQLoaded}
            placeholder="—"
            className="w-14 bg-neutral-800 border border-white/20 rounded px-2 py-1.5 text-white text-xs text-right focus:outline-none focus:border-brand-yellow disabled:opacity-40 placeholder:text-white/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          {showSaveMaxQ && (
            <button
              onClick={handleSaveMaxQ}
              disabled={savingMaxQ}
              className="px-2.5 py-1.5 rounded bg-brand-yellow text-black text-[10px] font-bold uppercase tracking-wide hover:brightness-95 transition-all disabled:opacity-50"
            >
              {savingMaxQ ? '…' : 'Save'}
            </button>
          )}
        </div>

        <div className="flex-1" />
        {/* When paused mid-run, show Continue + Restart side by side */}
        {hasStarted && !running && !allDone ? (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleToggle}
              className="px-5 py-2 rounded-lg font-bold text-sm tracking-wide transition-all bg-brand-yellow text-black hover:brightness-95"
            >
              ▶ Continue
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 rounded-lg font-bold text-sm tracking-wide transition-all bg-neutral-700 text-white hover:bg-neutral-600"
            >
              ↺ Restart
            </button>
          </div>
        ) : (
          <button
            onClick={allDone ? handleRestart : handleToggle}
            className={`px-5 py-2 rounded-lg font-bold text-sm tracking-wide transition-all flex-shrink-0 ${
              running
                ? 'bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500/30'
                : 'bg-brand-yellow text-black hover:brightness-95'
            }`}
          >
            {running ? '⏹ Stop' : allDone ? '↺ Restart' : '▶ Start'}
          </button>
        )}
      </header>

      {/* ── Four panels ── */}
      <div className="flex-1 flex overflow-hidden divide-x divide-white/10">
        {[0, 1, 2, 3].map((i) => (
          <div key={`${slug}-${i}`} className="flex-1 min-w-0 flex flex-col overflow-hidden bg-neutral-950">
            <SimulatorPanel slug={slug} panelIndex={i} simName={panelNames[i]} running={running} maxQuestions={maxQuestions} resetKey={resetKey} onRegression={handleRegression} onComplete={handleComplete} />
          </div>
        ))}
      </div>

      {/* ── Regression toast ── */}
      {regressionToast && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full pointer-events-auto" aria-live="polite">
          <div className="bg-red-950 border border-red-500 rounded-xl px-4 py-3 shadow-2xl">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-red-400 font-bold text-xs uppercase tracking-wider">
                ⚠️ Score regression (correct) — {regressionToast.length}
              </span>
              <button
                onClick={() => setRegressionToast(null)}
                className="text-red-300/60 hover:text-red-200 flex-shrink-0"
                aria-label="Dismiss"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="text-white text-xs font-mono mb-2">
              <span className="text-red-300">LO:</span> {regressionToast[0].testedLoCode}{'  '}
              <span className="text-red-300">Tested:</span>{' '}
              {regressionToast[0].testedCompetenceId.replace('Competence/', '')} · {regressionToast[0].testedType} · {regressionToast[0].testedLevel}
            </div>
            <div className="flex flex-col gap-1">
              {regressionToast.map((w, i) => {
                const code = w.competenceId.replace('Competence/', '')
                const LEVELS = ['Basic', 'Intermediate', 'Advanced', 'Highly advanced']
                return (
                  <div key={i} className="text-white text-xs font-mono border-t border-red-800/60 pt-1">
                    <div>
                      <span className="text-red-300">Affected:</span> {code} · {w.type}&nbsp;
                      <span className="text-red-300">Level:</span> {LEVELS[w.prevScore]} → <span className="text-red-400 font-bold">{LEVELS[w.newScore]}</span>
                    </div>
                    <div><span className="text-red-300">α before:</span> [{w.prevAlphas.map((a) => a.toFixed(2)).join(', ')}]</div>
                    <div><span className="text-red-300">α after: </span> [{w.newAlphas.map((a) => a.toFixed(2)).join(', ')}]</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
